import { CasesService } from '@/@types/generated'
import { CaseTopic } from '@/@types/generated/models/CaseTopic'
import { createContext, useContext, useEffect, useState } from 'react'

interface CaseTopicsContextProps {
  caseTopics: CaseTopic[]
}

const CaseTopicsContext = createContext<CaseTopicsContextProps>(undefined!)

export function useCaseTopics() {
  const context = useContext(CaseTopicsContext)
  if (context === undefined) {
    throw new Error(
      'useCaseTopics must be used within a CaseTopicsContext.Provider route'
    )
  }

  return context
}

export function CaseTopicsProvider({ children }: React.PropsWithChildren) {
  // State
  const [loading, setLoading] = useState(false)
  const [caseTopics, setCaseTopics] = useState<CaseTopic[]>([])

  // Load once
  useEffect(() => {
    const load = async () => {
      try {
        setCaseTopics(await getCaseTopics())
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  // No case yet, show loading
  if (loading) return <div>Retrieving case topics...</div>

  return (
    <CaseTopicsContext.Provider value={{ caseTopics }}>
      {children}
    </CaseTopicsContext.Provider>
  )
}

// Module-level cache and in-flight promise
const getCaseTopics: () => Promise<CaseTopic[]> = (() => {
  let cache: CaseTopic[] | null = null
  let inFlight: Promise<CaseTopic[]> | null = null

  return async () => {
    if (cache) return cache
    if (inFlight) return inFlight

    // Load from API
    inFlight = CasesService.listCaseTopics().then(({ caseTopics }) => {
      cache = caseTopics || []
      inFlight = null
      return cache
    })

    return inFlight
  }
})()
